import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import IconeEscola from '../assets/iconeescola.png';
import { 
  Clock, 
  Target, 
  CheckCircle2,
  XCircle,
  Award,
  ArrowLeft,
  Timer,
  FileText,
  AlertCircle,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to extract error messages from API responses
const getErrorMessage = (error, defaultMessage = "Ocorreu um erro") => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(detail)) {
      return detail[0]?.msg || defaultMessage;
    }
    // Handle string error messages
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return defaultMessage;
};

const Assessment = () => {
  const { user, token } = useAuth();
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previousResults, setPreviousResults] = useState([]);
  const [linkedCourse, setLinkedCourse] = useState(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  useEffect(() => {
    if (token && assessmentId) {
      fetchAssessmentData();
    }
  }, [token, assessmentId]);

  useEffect(() => {
    let interval = null;
    if (hasStarted && timeLeft > 0 && !showResults) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => {
          if (timeLeft <= 1) {
            handleSubmitAssessment();
            return 0;
          }
          return timeLeft - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [hasStarted, timeLeft, showResults]);

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/assessments/${assessmentId}`, { headers });
      const { assessment, questions } = response.data;
      
      setAssessment(assessment);
      setQuestions(questions);
      
      // Find course that links to this assessment
      try {
        const coursesResponse = await axios.get(`${API}/courses`, { headers });
        const linkedCourseData = coursesResponse.data.find(
          course => course.linked_assessment_id === assessmentId
        );
        if (linkedCourseData) {
          setLinkedCourse(linkedCourseData);
        }
      } catch (error) {
        console.error("Error fetching linked course:", error);
      }
      
      // Initialize answers object
      const initialAnswers = {};
      questions.forEach(q => {
        initialAnswers[q.id] = "";
      });
      setAnswers(initialAnswers);
      
    } catch (error) {
      console.error("Error fetching assessment:", error);
      toast.error("Erro ao carregar avaliação");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = () => {
    const now = new Date();
    setStartTime(now);
    setHasStarted(true);
    if (assessment.time_limit) {
      setTimeLeft(assessment.time_limit * 60); // Convert minutes to seconds
    }
    toast.success("Avaliação iniciada! Boa sorte!");
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitAssessment = async () => {
    if (isSubmitting) return;
    
    // Check if all questions are answered
    const unansweredQuestions = questions.filter(q => !answers[q.id] || answers[q.id].trim() === "");
    if (unansweredQuestions.length > 0) {
      const confirmed = window.confirm(
        `Você tem ${unansweredQuestions.length} questões não respondidas. Deseja continuar mesmo assim?`
      );
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const submissionData = {
        assessment_id: assessmentId,
        answers: answers,
        started_at: startTime
      };
      
      const response = await axios.post(`${API}/assessments/${assessmentId}/submit`, submissionData, { headers });
      
      setResults(response.data);
      setShowResults(true);
      
      if (response.data.passed) {
        toast.success("Parabéns! Você foi aprovado na avaliação!");
      } else {
        toast.error("Você não atingiu a nota mínima. Tente novamente mais tarde.");
      }
      
    } catch (error) {
      console.error("Error submitting assessment:", error);
      toast.error(getErrorMessage(error, "Erro ao enviar avaliação"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    const answeredQuestions = Object.values(answers).filter(answer => answer && answer.trim() !== "").length;
    return (answeredQuestions / questions.length) * 100;
  };

  const handleDownloadCertificate = async () => {
    if (!linkedCourse || downloadingCertificate) return;
    
    setDownloadingCertificate(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/pdf'
      };
      
      // Download course certificate since the user completed course + assessment
      const response = await axios.get(
        `${API}/certificates/course/${linkedCourse.id}/download`, 
        { 
          headers,
          responseType: 'blob' // Important for binary data
        }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado-${linkedCourse.title.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificado baixado com sucesso!");
      
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Erro ao baixar certificado. Tente novamente.");
    } finally {
      setDownloadingCertificate(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-300">Carregando avaliação...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Avaliação não encontrada</h2>
          <Button onClick={() => navigate("/dashboard")} className="btn btn-primary">
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      {/* Header */}
      <header className="navbar">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src={IconeEscola} 
              alt="Escola de Suporti" 
              className="h-20 w-auto" 
            />
            <div>
              <h1 className="nav-brand text-white">Avaliação</h1>
              <p className="text-sm text-gray-400">Sistema de Avaliações</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-white">{user.name}</p>
              <p className="text-sm text-gray-400">Avaliação em andamento</p>
            </div>
            
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {showResults ? (
          /* Results View */
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader className="text-center">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  results.passed ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {results.passed ? (
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  ) : (
                    <XCircle className="w-10 h-10 text-white" />
                  )}
                </div>
                
                <CardTitle className="text-2xl text-white mb-2">
                  {results.passed ? 'Aprovado!' : 'Não aprovado'}
                </CardTitle>
                
                <CardDescription className="text-gray-300">
                  {results.passed 
                    ? 'Parabéns! Você atingiu a nota mínima necessária.'
                    : 'Você não atingiu a nota mínima. Estude mais e tente novamente.'
                  }
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-800 rounded-lg">
                    <div className="text-2xl font-bold text-white">{results.percentage.toFixed(1)}%</div>
                    <div className="text-sm text-gray-400">Sua pontuação</div>
                  </div>
                  <div className="text-center p-4 bg-gray-800 rounded-lg">
                    <div className="text-2xl font-bold text-white">{assessment.passing_grade}%</div>
                    <div className="text-sm text-gray-400">Nota mínima</div>
                  </div>
                </div>
                
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-lg font-semibold text-white mb-2">
                    {results.score.toFixed(1)} / {results.total_points} pontos
                  </div>
                  <Progress 
                    value={results.percentage} 
                    className="w-full h-3"
                  />
                </div>

                <div className="flex justify-center gap-4">
                  <Button 
                    onClick={() => navigate("/dashboard")}
                    className="btn btn-primary"
                    data-testid="back-to-dashboard-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao Dashboard
                  </Button>
                  
                  {results.passed && linkedCourse && (
                    <Button 
                      onClick={handleDownloadCertificate}
                      disabled={downloadingCertificate}
                      variant="outline"
                      className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white disabled:opacity-50"
                      data-testid="download-certificate-btn"
                    >
                      {downloadingCertificate ? (
                        <>
                          <div className="loading-spinner w-4 h-4 mr-2" />
                          Baixando...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Baixar Certificado
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : !hasStarted ? (
          /* Pre-Assessment View */
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Target className="w-8 h-8 text-blue-500" />
                  {assessment.title}
                </CardTitle>
                <CardDescription className="text-gray-300 text-lg">
                  {assessment.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-400" />
                    <div>
                      <div className="text-lg font-semibold text-white">{questions.length}</div>
                      <div className="text-sm text-gray-400">Questões</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg">
                    <Clock className="w-6 h-6 text-green-400" />
                    <div>
                      <div className="text-lg font-semibold text-white">
                        {assessment.time_limit ? `${assessment.time_limit} min` : 'Sem limite'}
                      </div>
                      <div className="text-sm text-gray-400">Tempo limite</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg">
                    <Target className="w-6 h-6 text-purple-400" />
                    <div>
                      <div className="text-lg font-semibold text-white">{assessment.passing_grade}%</div>
                      <div className="text-sm text-gray-400">Nota mínima</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-orange-400" />
                    <div>
                      <div className="text-lg font-semibold text-white">{assessment.max_attempts}</div>
                      <div className="text-sm text-gray-400">Tentativas máx.</div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-300 mb-2">Instruções Importantes:</h3>
                  <ul className="space-y-1 text-sm text-blue-200">
                    <li>• Leia cada questão com atenção antes de responder</li>
                    <li>• Você pode navegar entre as questões livremente</li>
                    <li>• Suas respostas são salvas automaticamente</li>
                    {assessment.time_limit && (
                      <li>• A avaliação será enviada automaticamente quando o tempo acabar</li>
                    )}
                    <li>• Certifique-se de ter uma conexão estável com a internet</li>
                  </ul>
                </div>

                <div className="text-center">
                  <Button 
                    onClick={startAssessment}
                    className="btn btn-primary text-lg px-8 py-3"
                    data-testid="start-assessment-btn"
                  >
                    <Target className="w-5 h-5 mr-2" />
                    Iniciar Avaliação
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Assessment View */
          <div className="max-w-4xl mx-auto">
            {/* Progress Header */}
            <Card className="mb-6 bg-gray-900 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-white">
                      <span className="text-lg font-semibold">
                        Questão {currentQuestion + 1} de {questions.length}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Progress value={getProgressPercentage()} className="w-48 h-2" />
                      <span className="text-sm text-gray-400">
                        {Math.round(getProgressPercentage())}% concluído
                      </span>
                    </div>
                  </div>
                  
                  {timeLeft !== null && (
                    <div className="flex items-center gap-2">
                      <Timer className={`w-5 h-5 ${timeLeft < 300 ? 'text-red-400' : 'text-blue-400'}`} />
                      <span className={`text-lg font-mono ${timeLeft < 300 ? 'text-red-400' : 'text-white'}`}>
                        {formatTime(timeLeft)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Question Card */}
            {questions[currentQuestion] && (
              <Card className="mb-6 bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-white">
                    {questions[currentQuestion].question_text}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {questions[currentQuestion].options.map((option, index) => {
                    const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                    const isSelected = answers[questions[currentQuestion].id] === optionLetter;
                    
                    return (
                      <label
                        key={index}
                        className={`flex items-center p-4 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-900/30 text-white' 
                            : 'border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                        data-testid={`option-${optionLetter}`}
                      >
                        <input
                          type="radio"
                          name={`question-${questions[currentQuestion].id}`}
                          value={optionLetter}
                          checked={isSelected}
                          onChange={(e) => handleAnswerChange(questions[currentQuestion].id, e.target.value)}
                          className="mr-4 w-4 h-4"
                        />
                        <span className="text-lg">{option}</span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                data-testid="prev-question-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              
              <div className="flex gap-2">
                {questions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentQuestion(index)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                      index === currentQuestion
                        ? 'bg-blue-600 text-white'
                        : answers[questions[index]?.id] 
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    data-testid={`question-nav-${index}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              
              {currentQuestion === questions.length - 1 ? (
                <Button
                  onClick={handleSubmitAssessment}
                  disabled={isSubmitting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="submit-assessment-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner w-4 h-4 mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Finalizar
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))}
                  disabled={currentQuestion === questions.length - 1}
                  className="btn btn-primary"
                  data-testid="next-question-btn"
                >
                  Próxima
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Assessment;